/**
 * PATH: packages/job-hub/src/jobs/qa/QaPhotoSourceMenu.tsx
 * FILE_ID: JOBHUB-QA-PHOTO-SOURCE-MENU-001
 * PURPOSE: Photo or file evidence from camera, device, or the shared QDrive picker.
 * WHY: From QDrive must browse the shared picker (sort name/date), not search.
 * FLOW: menu -> camera | device input | QDriveFilePicker list -> attach.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, FolderOpen, HardDrive, Library, Loader2, Paperclip } from 'lucide-react'
import QDriveFilePicker from '@quotech/quote-builder/components/QDriveFilePicker'
import { MobileSheet, useCloseOnOutsidePointer, useIsMdUp } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'

import { QaCameraConfirmSheet } from './QaCameraConfirmSheet'
import type { QaJobFile } from './qaTypes'
import { QA_PHOTO_ACCEPT, QA_PHOTO_EXTENSIONS, fileReadyForUpload } from './qaPhotoAccept'

const MENU_WIDTH = 240
const MENU_HEIGHT = 168
const MENU_GAP = 4

export interface QaPhotoSourceMenuProps {
  mode?: 'photo' | 'file'
  onPickFile: (file: File) => void | Promise<void>
  onPickAsset: (assetId: string, filename?: string) => void | Promise<void>
  listQDriveFiles: (query?: string) => Promise<QaJobFile[]>
  listLibraryFiles?: () => Promise<QaJobFile[]>
  busy?: boolean
  className?: string
}

export function QaPhotoSourceMenu({
  mode = 'photo',
  onPickFile,
  onPickAsset,
  listQDriveFiles,
  listLibraryFiles,
  busy = false,
  className,
}: QaPhotoSourceMenuProps) {
  const [open, setOpen] = useState(false)
  const [qdriveOpen, setQdriveOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const cameraRef = useRef<HTMLInputElement | null>(null)
  const browseRef = useRef<HTMLInputElement | null>(null)
  const isPhoto = mode === 'photo'
  // Phone: an `absolute right-0 top-full` popover anchored near the bottom of
  // the viewport rendered off-screen (operator screenshot, 2026-09-15). A
  // bottom sheet is always fully on-screen. Desktop portals + flips instead.
  const isMdUp = useIsMdUp()

  const close = useCallback(() => setOpen(false), [])

  // Desktop: the trigger lives inside scrolling, `overflow-hidden` check
  // cards, so an absolutely-positioned popover gets clipped away or pushed
  // under the fold. Portal it to the body and place it from the trigger rect,
  // flipping above when there is no room below.
  const place = useCallback(() => {
    const trigger = wrapRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const height = menuRef.current?.offsetHeight || MENU_HEIGHT
    const flipUp = rect.bottom + MENU_GAP + height > window.innerHeight
      && rect.top - MENU_GAP - height > 0
    const top = flipUp ? rect.top - MENU_GAP - height : rect.bottom + MENU_GAP
    const left = Math.min(
      Math.max(MENU_GAP, rect.right - MENU_WIDTH),
      Math.max(MENU_GAP, window.innerWidth - MENU_WIDTH - MENU_GAP),
    )
    setAnchor({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!open || !isMdUp) {
      setAnchor(null)
      return
    }
    place()
    window.addEventListener('resize', place)
    // Capture phase: the trigger sits in nested scroll containers, and a
    // scroll event on an inner one does not bubble to window.
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, isMdUp, place])

  // Desktop popover only. The phone sheet is portaled outside wrapRef;
  // MobileSheet owns its own backdrop. Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open && isMdUp, close, [wrapRef, menuRef])

  const openQdrive = () => {
    close()
    setQdriveOpen(true)
  }
  const openLibrary = () => {
    close()
    setLibraryOpen(true)
  }

  const [cameraShot, setCameraShot] = useState<File | null>(null)
  const handleCamera = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    close()
    if (!file) return
    if (isMdUp) {
      void onPickFile(fileReadyForUpload(file))
      return
    }
    setCameraShot(fileReadyForUpload(file))
  }
  const handleInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // `multiple` on the browse input: one call to onPickFile per file, awaited
    // in turn, so every existing single-file consumer keeps working unchanged
    // and the uploads do not race each other onto the same check.
    // Reused from: packages/job-hub/src/jobs/qa/qaPhotoAccept.ts (fileReadyForUpload)
    const files = Array.from(event.target.files ?? []).map(fileReadyForUpload)
    event.target.value = ''
    close()
    for (const file of files) await onPickFile(file)
  }

  const sourceRow =
    'w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-foreground hover:bg-accent transition-colors'

  return (
    <div ref={wrapRef} className={cn('relative shrink-0', className)}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
        title={isPhoto ? 'Add photo' : 'Attach file'}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : isPhoto ? (
          <Camera className="h-4 w-4" aria-hidden />
        ) : (
          <Paperclip className="h-4 w-4" aria-hidden />
        )}
        <span className="hidden sm:inline">{isPhoto ? 'Photo' : 'Attach'}</span>
      </button>

      {open && isMdUp && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid="qa-photo-source-menu"
          style={{
            position: 'fixed',
            top: anchor?.top ?? 0,
            left: anchor?.left ?? 0,
            width: MENU_WIDTH,
            visibility: anchor ? 'visible' : 'hidden',
          }}
          className="z-[220] rounded-lg border border-border bg-popover shadow-lg overflow-hidden"
        >
          {isPhoto && (
            <button type="button" className={sourceRow} onClick={() => cameraRef.current?.click()}>
              <Camera className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              Take photo
            </button>
          )}
          <button type="button" className={sourceRow} onClick={() => browseRef.current?.click()}>
            <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            Choose files
          </button>
          <button type="button" className={sourceRow} onClick={openQdrive}>
            <HardDrive className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            From QDrive
          </button>
          {listLibraryFiles && (
            <button type="button" className={sourceRow} onClick={openLibrary}>
              <Library className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              QA library
            </button>
          )}
        </div>,
        document.body,
      )}

      {!isMdUp && (
        <MobileSheet
          open={open}
          onClose={close}
          title={isPhoto ? 'Add photo' : 'Attach file'}
          sheetTestId="qa-photo-source-sheet"
        >
          <div className="py-1">
            {isPhoto && (
              <button
                type="button"
                className="flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-sm"
                onClick={() => cameraRef.current?.click()}
              >
                <Camera className="h-4 w-4 text-muted-foreground" aria-hidden />
                Take photo
              </button>
            )}
            <button
              type="button"
              className="flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-sm"
              onClick={() => browseRef.current?.click()}
            >
              <FolderOpen className="h-4 w-4 text-muted-foreground" aria-hidden />
              Choose files
            </button>
            <button
              type="button"
              className="flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-sm"
              onClick={openQdrive}
            >
              <HardDrive className="h-4 w-4 text-muted-foreground" aria-hidden />
              From QDrive
            </button>
            {listLibraryFiles && (
              <button
                type="button"
                className="flex min-h-[44px] w-full items-center gap-2 px-4 text-left text-sm"
                onClick={openLibrary}
              >
                <Library className="h-4 w-4 text-muted-foreground" aria-hidden />
                QA library
              </button>
            )}
          </div>
        </MobileSheet>
      )}

      {isPhoto && (
        <input
          ref={cameraRef}
          type="file"
          accept={QA_PHOTO_ACCEPT}
          capture="environment"
          className="hidden"
          onChange={handleCamera}
        />
      )}
      <input
        ref={browseRef}
        type="file"
        multiple
        accept={isPhoto ? QA_PHOTO_ACCEPT : undefined}
        className="hidden"
        onChange={handleInput}
      />

      <QaCameraConfirmSheet
        file={cameraShot}
        onClose={() => setCameraShot(null)}
        onRetake={() => {
          setCameraShot(null)
          cameraRef.current?.click()
        }}
        onUse={(file) => {
          setCameraShot(null)
          void onPickFile(file)
        }}
      />
      <QDriveFilePicker
        open={qdriveOpen}
        title={isPhoto ? 'Choose a photo from QDrive' : 'Choose a file from QDrive'}
        description="Browse QDrive, sort by name or date, then attach. Filter stays on this list."
        acceptExtensions={isPhoto ? QA_PHOTO_EXTENSIONS : undefined}
        onClose={() => setQdriveOpen(false)}
        loadFiles={listQDriveFiles}
        multiSelect
        onSelect={async (file) => {
          await onPickAsset(file.id, file.filename)
        }}
        onSelectMany={async (files) => {
          for (const file of files) await onPickAsset(file.id, file.filename)
        }}
      />
      {listLibraryFiles && (
        <QDriveFilePicker
          open={libraryOpen}
          title="QA library"
          description="Files already on this job. Pick one to attach."
          acceptExtensions={isPhoto ? QA_PHOTO_EXTENSIONS : undefined}
          onClose={() => setLibraryOpen(false)}
          loadFiles={listLibraryFiles}
          multiSelect
          onSelect={async (file) => {
            // Reused from: packages/quote-builder/src/components/QDriveFilePicker.tsx (QDriveFilePicker)
            await onPickAsset(file.id, file.filename)
          }}
          onSelectMany={async (files) => {
            for (const file of files) await onPickAsset(file.id, file.filename)
          }}
        />
      )}
    </div>
  )
}
